// Recherche + filtres des SKILLCARDS — partagés par l'app (/tutor/cards,
// /student/skillcards) et le site (/skillcards). Purs et testés.
//
// Recherche insensible à la casse et aux accents, plusieurs mots = ET (dans
// n'importe quel ordre), sur le titre (LaTeX retiré), le domaine et des champs
// additionnels (sujet, règle…). Domaine = `domain_label` (« Géométrie »…).

import { SCHOOL_LEVELS } from '../index';
import { stripMath } from './math-text';

export interface CardFilterable {
  title: string;
  levels: readonly string[];
  domainLabel: string | null;
  /** Autres textes cherchables (sujet, règle, méthode…). */
  extra?: readonly (string | null | undefined)[];
}

export interface CardFilters {
  q?: string;
  level?: string;
  domain?: string;
}

export function normalizeCardSearch(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[-'’]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function matchesCardFilters(card: CardFilterable, filters: CardFilters): boolean {
  if (filters.level && !card.levels.includes(filters.level)) return false;
  if (
    filters.domain &&
    normalizeCardSearch(card.domainLabel ?? '') !== normalizeCardSearch(filters.domain)
  )
    return false;
  const words = normalizeCardSearch(filters.q ?? '')
    .split(' ')
    .filter(Boolean);
  if (words.length === 0) return true;
  const haystack = normalizeCardSearch(
    [stripMath(card.title), card.domainLabel ?? '', ...(card.extra ?? [])]
      .filter(Boolean)
      .join(' '),
  );
  return words.every((w) => haystack.includes(w));
}

export function filterCards<T>(
  items: readonly T[],
  toFilterable: (item: T) => CardFilterable,
  filters: CardFilters,
): T[] {
  return items.filter((it) => matchesCardFilters(toFilterable(it), filters));
}

/** Domaines présents (avec effectif), triés par effectif puis par nom. */
export function cardDomainOptions(
  cards: readonly Pick<CardFilterable, 'domainLabel'>[],
): { value: string; count: number }[] {
  const m = new Map<string, number>();
  for (const c of cards) {
    const d = c.domainLabel?.trim();
    if (d) m.set(d, (m.get(d) ?? 0) + 1);
  }
  return [...m.entries()]
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value, 'fr'));
}

/** Niveaux présents, dans l'ordre scolaire, avec leur libellé. */
export function cardLevelOptions(
  cards: readonly Pick<CardFilterable, 'levels'>[],
): { value: string; label: string; count: number }[] {
  return SCHOOL_LEVELS.map((l) => ({
    value: l.value as string,
    label: l.label,
    count: cards.filter((c) => c.levels.includes(l.value)).length,
  })).filter((o) => o.count > 0);
}
